import { ProductionKeyPerformanceIndicator } from './production-key-performance-indicator.model';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import { ConfiguredApplicationsEvaluationData } from './configured-applications-kpi-data.model';
import { CompanyProductDeploysFrequencyEvaluationData } from './company-product-deploys-frequency-kpi-data.model';
import { SprintPlanningExecutionData } from './sprint-planning-execution-kpi-data.model';
import { SprintReviewExecutionData } from './sprint-review-execution-kpi-data.model';
import { CompanyProductExpectedVsRegisteredWorkGeneralKpiDetail } from './company-product-expected-vs-registered-work-kpi-data.model';
import { CompanyProductPhaseTimeComplianceGeneralKpiDetail } from './company-product-phase-time-compliance-kpi-data.model';
import { HoldedConfiguredKpiData } from './holded-configured-kpi-data.model';
import { IssuesRatioCheckKpiData } from './issues-ratio-check-kpi-data.model';
import { ScheduleAndRemoteWorkComplianceEvaluatorData } from './schedule-and-remote-work-compliance-kpi-data.model';
import { RegisteredWorkComplianceEvaluatorData } from './registered-work-compliance-kpi-data.model';
import { UptimeMonitorsKpiData } from './uptime-monitors-kpi-data.model';
import { SharePresenceWithColleaguesEvaluatorData } from './share-presence-with-colleagues-kpi-data.model';
import { MeetingAttendanceEvaluatorData } from './meeting-attendance-kpi-data.model';
import { CompanyProductDailyStandupEvaluationData } from './company-product-daily-standup-compliance-kpi-data.model';

export interface KpiEvaluationStyle {
  color: string;
  bgColor: string;
  label: string;
}

const DEFAULT_EVALUATION_STYLE: KpiEvaluationStyle = {
  color: 'var(--grey_text_color)',
  bgColor: 'var(--grey_soft_background_color)',
  label: '',
};

export const KPI_EVALUATION_STYLES: Record<ProductionKeyPerformanceIndicatorEvaluationEnum, KpiEvaluationStyle> = {
  [ProductionKeyPerformanceIndicatorEvaluationEnum.Good]: {
    color: 'var(--green)',
    bgColor: 'var(--green-lighter)',
    label: 'Excelente',
  },
  [ProductionKeyPerformanceIndicatorEvaluationEnum.Medium]: {
    color: 'var(--yellow)',
    bgColor: 'var(--yellow-lighter)',
    label: 'Aceptable',
  },
  [ProductionKeyPerformanceIndicatorEvaluationEnum.Poor]: {
    color: 'var(--warn-color)',
    bgColor: 'var(--warn-lighter-color)',
    label: 'Mejorable',
  },
  [ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable]: {
    color: 'var(--grey_text_color)',
    bgColor: 'var(--grey_soft_background_color)',
    label: 'No calculable',
  },
};

export function getKpiEvaluationStyle(evaluation: ProductionKeyPerformanceIndicatorEvaluationEnum): KpiEvaluationStyle {
  return KPI_EVALUATION_STYLES[evaluation] ?? { ...DEFAULT_EVALUATION_STYLE, label: String(evaluation) };
}

export function extractTooltipSummary(evaluation: ProductionKeyPerformanceIndicatorEvaluation | null | undefined): string | null {
  const data = evaluation?.data;
  if (data === null || data === undefined || typeof data !== 'object') {
    return null;
  }

  const summary = (data as { tooltip_summary?: unknown }).tooltip_summary;
  return typeof summary === 'string' ? summary : null;
}

export interface ProductionKeyPerformanceIndicatorEvaluationSummary {
  month_start_date: string;
  month_end_date: string;
  percentage: number | null;
  evaluation: ProductionKeyPerformanceIndicatorEvaluationEnum;
}

export interface ProductionKeyPerformanceIndicatorEvaluation extends ProductionKeyPerformanceIndicatorEvaluationSummary {
  data:
    | RegisteredWorkComplianceEvaluatorData
    | ScheduleAndRemoteWorkComplianceEvaluatorData
    | SharePresenceWithColleaguesEvaluatorData
    | MeetingAttendanceEvaluatorData
    | CompanyProductDailyStandupEvaluationData
    | CompanyProductDeploysFrequencyEvaluationData
    | UptimeMonitorsKpiData
    | HoldedConfiguredKpiData
    | CompanyProductPhaseTimeComplianceGeneralKpiDetail
    | ConfiguredApplicationsEvaluationData
    | SprintReviewExecutionData
    | SprintPlanningExecutionData
    | CompanyProductExpectedVsRegisteredWorkGeneralKpiDetail
    | IssuesRatioCheckKpiData
    | null;
}

export type ProductionKeyPerformanceIndicatorEvaluationMonth =
  | ProductionKeyPerformanceIndicatorEvaluation
  | ProductionKeyPerformanceIndicatorEvaluationSummary;

export interface ProductionKeyPerformanceIndicatorEvaluationWithData {
  company_product_id: number | null;
  company_product: {
    id: number;
    name: string;
    logo_url: string;
  } | null;
  production_key_performance_indicator: ProductionKeyPerformanceIndicator;
  tolerance_seconds: number | null;
  data: ProductionKeyPerformanceIndicatorEvaluation[] | null;
  has_exception: boolean;
}

export interface CompanyProductKpiEvaluationsGroup {
  company_product: {
    id: number;
    name: string;
    logo_url: string;
  };
  company_product_kpis: ProductionKeyPerformanceIndicatorEvaluationWithData[];
  my_company_product_kpis: ProductionKeyPerformanceIndicatorEvaluationWithData[];
}

export interface KpiEvaluationsResponse {
  evaluations: ProductionKeyPerformanceIndicatorEvaluationWithData[];
  company_product_groups: CompanyProductKpiEvaluationsGroup[];
  start_date: string;
  end_date: string;
}
